import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Pause, 
  X, 
  CaretDown,
  Rewind,
  FastForward,
  Timer,
  ShareNetwork,
  Export
} from '@phosphor-icons/react';
import { useAudioPlayer } from '@/contexts/AudioPlayerContext';
import { useState, useRef, useCallback } from 'react';

/**
 * Format time in m:ss format
 */
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * FullScreenPlayer Component
 * Full screen audio player matching mobile app design (IMG_5828)
 */
const FullScreenPlayer = () => {
  const {
    currentTrack,
    isPlaying,
    isLoading,
    isFullScreen,
    currentTime,
    duration,
    playbackRate,
    togglePlayPause,
    seekTo,
    skipForward,
    skipBackward,
    toggleFullScreen,
    setPlaybackRate,
    hidePlayer,
  } = useAudioPlayer();

  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const progressBarRef = useRef(null);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

  // Calculate seek position from mouse/touch event
  const calculateSeekPosition = useCallback((clientX) => {
    if (!progressBarRef.current || !duration) return null;
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = x / rect.width;
    return percentage * duration;
  }, [duration]);

  // Handle click on progress bar
  const handleProgressClick = (e) => {
    const newTime = calculateSeekPosition(e.clientX);
    if (newTime !== null) {
      seekTo(newTime);
    }
  };

  // Handle drag start
  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const handleDragMove = (moveEvent) => {
      const clientX = moveEvent.type === 'touchmove' 
        ? moveEvent.touches[0].clientX 
        : moveEvent.clientX;
      const newTime = calculateSeekPosition(clientX);
      if (newTime !== null) {
        seekTo(newTime);
      }
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('touchend', handleDragEnd);
    };

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchmove', handleDragMove);
    document.addEventListener('touchend', handleDragEnd);
  };

  // Handle skip backward 15 seconds
  const handleSkipBackward = () => {
    const newTime = Math.max(0, currentTime - 15);
    seekTo(newTime);
  };

  // Handle skip forward 15 seconds
  const handleSkipForward = () => {
    const newTime = Math.min(duration, currentTime + 15);
    seekTo(newTime);
  };

  if (!isFullScreen || !currentTrack) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed inset-0 z-[100] bg-black overflow-hidden flex flex-col"
        data-testid="full-screen-player"
      >
        {/* Header - Minimize, Now Playing, Close */}
        <div className="flex items-center justify-between px-4 py-4 flex-shrink-0">
          {/* Minimize Button */}
          <button
            onClick={toggleFullScreen}
            className="w-12 h-12 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
            data-testid="minimize-player"
          >
            <CaretDown size={28} className="text-white" />
          </button>
          
          {/* Now Playing Badge */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-[#C4956A]">
              {/* Equalizer Icon */}
              <div className="flex items-end gap-0.5 h-4">
                <motion.div 
                  animate={isPlaying ? { height: ['40%', '100%', '60%', '80%', '40%'] } : { height: '40%' }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="w-1 bg-[#C4956A] rounded-full"
                />
                <motion.div 
                  animate={isPlaying ? { height: ['80%', '40%', '100%', '50%', '80%'] } : { height: '60%' }}
                  transition={{ duration: 0.8, repeat: Infinity, delay: 0.1 }}
                  className="w-1 bg-[#C4956A] rounded-full"
                />
                <motion.div 
                  animate={isPlaying ? { height: ['60%', '90%', '40%', '100%', '60%'] } : { height: '80%' }}
                  transition={{ duration: 0.8, repeat: Infinity, delay: 0.2 }}
                  className="w-1 bg-[#C4956A] rounded-full"
                />
              </div>
              <span className="font-medium">Now Playing</span>
            </div>
          </div>
          
          {/* Close Button - Red */}
          <button
            onClick={hidePlayer}
            className="w-12 h-12 rounded-full bg-[#8B2942] hover:bg-[#A03050] flex items-center justify-center transition-colors"
            data-testid="close-player"
          >
            <X size={24} weight="bold" className="text-white" />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 overflow-hidden">
          {/* Cover Art - Large with rounded corners */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-2xl mb-8 flex-shrink-0"
          >
            <img
              src={currentTrack.cover_image_url || 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=600'}
              alt={currentTrack.title}
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Track Info - Centered */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center w-full max-w-md mb-6 flex-shrink-0"
          >
            <h2 className="text-white text-2xl sm:text-3xl font-serif mb-2 px-4">
              {currentTrack.title}
            </h2>
            <p className="text-gray-400 text-base">
              {currentTrack.winery_name || currentTrack.title}
            </p>
          </motion.div>

          {/* Progress Bar - Clickable and Draggable */}
          <div className="w-full max-w-md px-4 mb-6 flex-shrink-0">
            <div
              ref={progressBarRef}
              className="h-2 bg-gray-700 rounded-full cursor-pointer relative group"
              onClick={handleProgressClick}
            >
              {/* Progress Fill */}
              <div
                className="h-full bg-gray-400 rounded-full relative"
                style={{ width: `${progress}%` }}
              >
                {/* Scrubber Handle - Draggable */}
                <div 
                  className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-4 bg-white rounded-full shadow-lg cursor-grab active:cursor-grabbing transition-transform ${isDragging ? 'scale-125' : 'hover:scale-110'}`}
                  onMouseDown={handleDragStart}
                  onTouchStart={handleDragStart}
                />
              </div>
            </div>
            
            {/* Time Display */}
            <div className="flex justify-between mt-3 text-gray-400 text-sm">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Main Controls - Skip 15, Play/Pause, Skip 15 */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex items-center justify-center gap-8 sm:gap-12 mb-8 flex-shrink-0"
          >
            {/* Skip Back 15 */}
            <button
              onClick={handleSkipBackward}
              className="p-3 hover:bg-white/10 rounded-full transition-colors active:scale-95"
              data-testid="skip-back"
            >
              <div className="relative flex flex-col items-center">
                <Rewind size={40} className="text-white" weight="fill" />
                <span className="text-xs text-white mt-1 font-medium">15</span>
              </div>
            </button>

            {/* Play/Pause - Large brown/wine oval button */}
            <button
              onClick={togglePlayPause}
              disabled={isLoading}
              className="w-24 h-16 sm:w-28 sm:h-18 rounded-full bg-[#8B4513] hover:bg-[#A0522D] flex items-center justify-center transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
              data-testid="play-pause"
            >
              {isLoading ? (
                <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause size={40} weight="fill" className="text-white" />
              ) : (
                <Play size={40} weight="fill" className="text-white ml-1" />
              )}
            </button>

            {/* Skip Forward 15 */}
            <button
              onClick={handleSkipForward}
              className="p-3 hover:bg-white/10 rounded-full transition-colors active:scale-95"
              data-testid="skip-forward"
            >
              <div className="relative flex flex-col items-center">
                <FastForward size={40} className="text-white" weight="fill" />
                <span className="text-xs text-white mt-1 font-medium">15</span>
              </div>
            </button>
          </motion.div>

          {/* Bottom Controls - Share, Timer/Speed, Export */}
          <div className="flex items-center justify-center gap-12 w-full max-w-md px-4 flex-shrink-0">
            {/* Share/Download */}
            <button className="p-3 hover:bg-white/10 rounded-full transition-colors">
              <ShareNetwork size={26} className="text-white" />
            </button>

            {/* Speed Control */}
            <div className="relative">
              <button
                onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                className="p-3 hover:bg-white/10 rounded-full transition-colors flex items-center gap-1"
              >
                <Timer size={26} className="text-white" />
                <span className="text-white text-xs">{playbackRate}x</span>
              </button>
              
              {showSpeedMenu && (
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden min-w-[80px]">
                  {speedOptions.map((speed) => (
                    <button
                      key={speed}
                      onClick={() => {
                        setPlaybackRate(speed);
                        setShowSpeedMenu(false);
                      }}
                      className={`block w-full px-4 py-2 text-sm text-center hover:bg-gray-700 transition-colors ${
                        playbackRate === speed ? 'text-[#C4956A]' : 'text-white'
                      }`}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Export */}
            <button className="p-3 hover:bg-white/10 rounded-full transition-colors">
              <Export size={26} className="text-white" />
            </button>
          </div>

          {/* Preview Notice */}
          {currentTrack.is_preview && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-6 text-center px-4 flex-shrink-0"
            >
              <p className="text-[#C4956A] text-sm mb-1">This is a 30-second preview</p>
              <p className="text-gray-500 text-xs">Purchase the full tour to unlock the complete audio</p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FullScreenPlayer;
